import React, { useState, useRef, useEffect, useCallback } from 'react';
import apiClient from '../api';
import '../styles/github.css';

function HandwritingPanel({ memoId, onSave, onCancel, theme = 'dark' }) {
  const canvasRef = useRef(null); // 主画布
  const contextRef = useRef(null); // 主画布上下文
  const drawingCanvasRef = useRef(null); // 离屏画布（专门绘制手写）
  const drawingContextRef = useRef(null); // 离屏画布上下文
  
  const [isDrawing, setIsDrawing] = useState(false);
  const [lineWidth, setLineWidth] = useState(3);
  const [lineColor, setLineColor] = useState('#ffffff');
  const [backgroundColor, setBackgroundColor] = useState(theme === 'dark' ? '#0d1117' : '#ffffff');
  const [drawingMode, setDrawingMode] = useState('pen'); // 'pen', 'eraser'
  const [drawingHistory, setDrawingHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [title, setTitle] = useState('');
  const [showGrid, setShowGrid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // 新增：分类相关状态
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  
  // 预定义颜色
  const colorPalette = [
    '#ffffff', '#000000', '#ff0000', '#00ff00', '#0000ff', 
    '#ffff00', '#ff00ff', '#00ffff', '#ff8800', '#8800ff',
    '#008888', '#888800', '#880000', '#008800', '#000088'
  ];
  
  // 背景颜色预设
  const backgroundColors = [
    { name: '深色', value: '#0d1117' },
    { name: '浅色', value: '#ffffff' },
    { name: '米黄', value: '#f5f0e6' },
    { name: '浅灰', value: '#f6f8fa' },
    { name: '天蓝', value: '#f0f8ff' },
    { name: '淡绿', value: '#f0fff0' },
    { name: '淡紫', value: '#f8f0ff' }
  ];
  
  // 笔画粗细选项
  const brushSizes = [1, 2, 3, 5, 8, 12, 16, 20];

  // 获取坐标
  const getCoordinates = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { offsetX: 0, offsetY: 0 };
    
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    
    if (e.type.includes('touch')) {
      // 触摸事件
      if (!e.touches || e.touches.length === 0) {
        return { offsetX: 0, offsetY: 0 };
      }
      const touch = e.touches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      // 鼠标事件
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    // 计算相对于画布的位置
    const offsetX = clientX - rect.left;
    const offsetY = clientY - rect.top;
    
    return { 
      offsetX: offsetX * (canvas.width / rect.width) / 2,
      offsetY: offsetY * (canvas.height / rect.height) / 2
    };
  }, []);


// 绘制网格
  const drawGrid = useCallback((ctx, width, height) => {
    if (!ctx) return;
    
    const gridSize = 20;
    const gridColor = backgroundColor === '#0d1117' || backgroundColor === '#161b22' 
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)';
    
    ctx.save();
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.5;
    ctx.globalCompositeOperation = 'source-over';
    
    // 绘制垂直线
    for (let x = 0; x <= width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
    }
    
    // 绘制水平线
    for (let y = 0; y <= height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
    }
    
    ctx.restore();
  }, [backgroundColor]);

// 初始化双画布
  const initCanvases = useCallback(() => {
    const mainCanvas = canvasRef.current;
    if (!mainCanvas) {
        console.error('画布元素不存在');
        return;
    }
    
    // 获取画布显示尺寸
    const displayWidth = mainCanvas.clientWidth;
    const displayHeight = mainCanvas.clientHeight;

    console.log('画布初始化 - 显示尺寸:', displayWidth, 'x', displayHeight);
    console.log('是否为编辑模式:', memoId ? '是' : '否');
    
    // 设置主画布实际尺寸为显示尺寸的2倍（为了高DPI显示）
    mainCanvas.width = displayWidth * 2;
    mainCanvas.height = displayHeight * 2;
    
    // 初始化主画布上下文
    const mainCtx = mainCanvas.getContext('2d', { willReadFrequently: true });
    
    // 重置变换矩阵
    mainCtx.setTransform(1, 0, 0, 1, 0, 0);
    mainCtx.scale(2, 2);
    
    mainCtx.lineCap = 'round';
    mainCtx.lineJoin = 'round';
    mainCtx.fillStyle = backgroundColor;
    
    // 清除并填充背景
    mainCtx.clearRect(0, 0, displayWidth, displayHeight);
    mainCtx.fillStyle = backgroundColor;
    mainCtx.fillRect(0, 0, displayWidth, displayHeight);

      // 立即绘制网格（如果已开启）
    if (showGrid) {
        drawGrid(mainCtx, displayWidth, displayHeight);
    }
    
    contextRef.current = mainCtx;
    
    // 创建离屏画布专门绘制手写内容
    const drawingCanvas = document.createElement('canvas');
    drawingCanvas.width = displayWidth * 2;
    drawingCanvas.height = displayHeight * 2;
    const drawingCtx = drawingCanvas.getContext('2d');
    
    // 设置离屏画布变换和绘图属性
    drawingCtx.setTransform(1, 0, 0, 1, 0, 0);
    drawingCtx.scale(2, 2);
    drawingCtx.lineCap = 'round';
    drawingCtx.lineJoin = 'round';
    drawingCtx.strokeStyle = lineColor;
    drawingCtx.lineWidth = lineWidth;
    
    // 确保离屏画布背景透明
    drawingCtx.clearRect(0, 0, displayWidth, displayHeight);
    
    drawingCanvasRef.current = drawingCanvas;
    drawingContextRef.current = drawingCtx;
    
    console.log('双画布初始化完成');
  }, [backgroundColor, showGrid,lineColor, lineWidth,memoId]);

// 将离屏画布内容绘制到主画布
  const redrawToMainCanvas = useCallback(() => {
    const mainCtx = contextRef.current;
    const mainCanvas = canvasRef.current;
    const drawingCanvas = drawingCanvasRef.current;
    
    if (!mainCtx || !mainCanvas || !drawingCanvas) {
      console.log('重绘失败: 缺少必要的canvas引用');
      return;
    }
    
    const displayWidth = mainCanvas.clientWidth;
    const displayHeight = mainCanvas.clientHeight;
    
    // 清空主画布并填充背景色
    mainCtx.clearRect(0, 0, displayWidth, displayHeight);
    mainCtx.fillStyle = backgroundColor;
    mainCtx.fillRect(0, 0, displayWidth, displayHeight);
    
    // 将离屏画布内容绘制到主画布
    mainCtx.drawImage(drawingCanvas, 0, 0, displayWidth, displayHeight);
    
    // 重新绘制网格
    if (showGrid) {
      console.log('绘制网格');
      drawGrid(mainCtx, displayWidth, displayHeight);
    }
    
    console.log('已重绘主画布');
  }, [backgroundColor, showGrid, drawGrid]);

// 开始绘制
  const startDrawing = useCallback((e) => {
    e.preventDefault();
    const { offsetX, offsetY } = getCoordinates(e);
    const ctx = drawingContextRef.current;
    
    if (!ctx) {
      console.log('无法开始绘制: drawingContextRef.current 为空');
      return;
    }
    
    setIsDrawing(true);
    
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY);
    
    // 设置绘图属性
    if (drawingMode === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = lineWidth * 3;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = lineWidth;
    }
    
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [getCoordinates, drawingMode, lineColor, lineWidth]);

// 绘制中
  const draw = useCallback((e) => {
    e.preventDefault();
    
    if (!isDrawing || !drawingContextRef.current) {
      return;
    }
    
    const { offsetX, offsetY } = getCoordinates(e);
    const ctx = drawingContextRef.current;
    
    // 更新绘图属性（确保使用当前的画笔设置）
    if (drawingMode === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = lineWidth * 3;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = lineWidth;
    }
    
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
    
    // 立即将手写内容绘制到主画布上
    redrawToMainCanvas();
  }, [isDrawing, drawingMode, lineColor, lineWidth, getCoordinates, redrawToMainCanvas]);

// 停止绘制
  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;
    
    const ctx = drawingContextRef.current;
    if (ctx) {
      ctx.closePath();
    }
    
    setIsDrawing(false);
    
    // 保存到历史记录
    saveToHistory();
  }, [isDrawing]);

  // 保存到历史记录
  const saveToHistory = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // 保存主画布当前状态
    const dataUrl = canvas.toDataURL('image/png');
    
    // 只保留最近20个历史记录
    const newHistory = [...drawingHistory.slice(0, historyIndex + 1), dataUrl].slice(-20);
    setDrawingHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [drawingHistory, historyIndex]);

// 更新 drawingContext 的画笔设置
  useEffect(() => {
    const ctx = drawingContextRef.current;
    if (ctx) {
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = lineWidth;
    }
  }, [lineColor, lineWidth]);


// 主要初始化 
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    console.log('HandwritingPanel 组件挂载，memoId:', memoId);
    
    // 重置状态
    setDrawingHistory([]);
    setHistoryIndex(-1);
    
    // 初始化双画布
    initCanvases();
    
    // 获取分类列表
    fetchCategories();
    
    // 如果是编辑已有备忘录，加载现有手写内容
    if (memoId) {
        console.log('延迟加载现有手写内容...');
        // 延迟加载，确保画布完全初始化
        setTimeout(() => {
            loadExistingDrawing();
        }, 800);
    } else {
        // 保存初始状态到历史记录
        setTimeout(() => {
            saveToHistory();
        }, 200);
    }
    
    // 监听窗口大小变化
    const handleResize = () => {
        if (canvasRef.current) {
        console.log('窗口大小变化，重新初始化画布');
        initCanvases();
        
        // 如果是编辑模式，重新加载图片
        if (memoId && drawingHistory.length > 0) {
            setTimeout(() => {
            const img = new Image();
            img.onload = () => {
              const mainCtx = contextRef.current;
              const mainCanvas = canvasRef.current;
              const drawingCtx = drawingContextRef.current;
                
              if (mainCtx && mainCanvas && drawingCtx) {
                const displayWidth = mainCanvas.clientWidth;
                const displayHeight = mainCanvas.clientHeight;
                
                // 清空并重新绘制
                drawingCtx.clearRect(0, 0, displayWidth, displayHeight);
                drawingCtx.drawImage(img, 0, 0, displayWidth, displayHeight);
                redrawToMainCanvas();
              }
            };
            img.src = drawingHistory[historyIndex];
          }, 100);
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
        console.log('清理 HandwritingPanel 组件');
        window.removeEventListener('resize', handleResize);
    };
  }, [memoId]); // 只依赖 memoId

  // 获取分类列表
  const fetchCategories = async () => {
    setCategoryLoading(true);
    try {
      const response = await apiClient.get('/categories');
      setAvailableCategories(response.data);
    } catch (err) {
      console.error('获取分类失败:', err);
    } finally {
      setCategoryLoading(false);
    }
  };


// 加载现有手写内容
  const loadExistingDrawing = async () => {
    try {
        console.log('开始加载手写内容，memoId:', memoId);
        
        const response = await apiClient.get(`/memos/${memoId}`);
        const memo = response.data;
        
        console.log('API响应成功:', {
        hasData: !!memo.handwriting_data,
        dataLength: memo.handwriting_data?.length,
        title: memo.title
        });
        
        setTitle(memo.title || '');
        setSelectedCategoryId(memo.category_id || null);
        
        if (memo.handwriting_data) {
        console.log('开始加载图片...');
        
        const img = new Image();
        
        img.onload = () => {
            console.log('图片加载成功，尺寸:', img.width, 'x', img.height);
            
            const mainCanvas = canvasRef.current;
            const mainCtx = contextRef.current;
            const drawingCtx = drawingContextRef.current;
            
            if (!mainCanvas || !mainCtx || !drawingCtx) {
            console.error('画布或上下文缺失:', {
                canvas: !!mainCanvas,
                mainCtx: !!mainCtx,
                drawingCtx: !!drawingCtx
            });
            setIsLoading(false);
            return;
            }
            
            const displayWidth = mainCanvas.clientWidth;
            const displayHeight = mainCanvas.clientHeight;
            
            console.log('画布显示尺寸:', displayWidth, 'x', displayHeight);
            
            try {
            // 清空离屏画布
            drawingCtx.clearRect(0, 0, displayWidth, displayHeight);
            
            // 将图片绘制到离屏画布
            drawingCtx.drawImage(img, 0, 0, displayWidth, displayHeight);
            console.log('图片已绘制到离屏画布');
            
            // 立即重绘到主画布
            redrawToMainCanvas();
            
            // 保存到历史记录
            setTimeout(() => {
                saveToHistory();
                console.log('已保存到历史记录');
                setIsLoading(false);
            }, 100);
            
            } catch (drawError) {
            console.error('绘制图片时出错:', drawError);
            setIsLoading(false);
            }
        };
        
        img.onerror = (error) => {
            console.error('图片加载失败:', error);
            setIsLoading(false);
        };
        
        img.src = memo.handwriting_data;
        
        } else {
        console.log('没有手写数据');
        setIsLoading(false);
        }
        
    } catch (err) {
        console.error('加载失败:', err);
        setIsLoading(false);
    }
  };

// 重做
  const redo = useCallback(() => {
    if (historyIndex >= drawingHistory.length - 1) return;
    
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    
    const img = new Image();
    img.onload = () => {
      const mainCtx = contextRef.current;
      const mainCanvas = canvasRef.current;
      const drawingCtx = drawingContextRef.current;
      const drawingCanvas = drawingCanvasRef.current;
      
      if (mainCtx && mainCanvas && drawingCtx && drawingCanvas) {
        const displayWidth = mainCanvas.clientWidth;
        const displayHeight = mainCanvas.clientHeight;
        
        // 清空离屏画布
        drawingCtx.clearRect(0, 0, displayWidth, displayHeight);
        
        // 将历史图像绘制到离屏画布
        drawingCtx.drawImage(img, 0, 0, displayWidth, displayHeight);
        
        // 重绘到主画布
        redrawToMainCanvas();
      }
    };
    img.src = drawingHistory[newIndex];
  }, [drawingHistory, historyIndex, redrawToMainCanvas]);

// 清空画布
  const clearCanvas = () => {
    const mainCtx = contextRef.current;
    const drawingCtx = drawingContextRef.current;
    const mainCanvas = canvasRef.current;
    
    if (mainCtx && drawingCtx && mainCanvas) {
      const displayWidth = mainCanvas.clientWidth;
      const displayHeight = mainCanvas.clientHeight;
      
      // 清空离屏画布
      drawingCtx.clearRect(0, 0, displayWidth, displayHeight);
      
      // 清空主画布并填充背景
      mainCtx.clearRect(0, 0, displayWidth, displayHeight);
      mainCtx.fillStyle = backgroundColor;
      mainCtx.fillRect(0, 0, displayWidth, displayHeight);
      
      // 重新绘制网格
      if (showGrid) {
        drawGrid(mainCtx, displayWidth, displayHeight);
      }
      
      saveToHistory();
    }
  };

// 修改 toggleGrid 函数
  const toggleGrid = () => {
    const newShowGrid = !showGrid;
    console.log('切换网格显示:', newShowGrid);
    setShowGrid(newShowGrid);
    
    // 立即重绘画布
    redrawToMainCanvas();
    saveToHistory();
    
    // 额外触发一次重绘确保效果
    setTimeout(() => {
        redrawToMainCanvas();
    }, 10);
  };

// 改变背景色
  const changeBackgroundColor = (newColor) => {
    console.log('切换背景色:', newColor);
    
    // 更新背景色状态
    setBackgroundColor(newColor);
    
    // 立即重绘画布
    const mainCtx = contextRef.current;
    const mainCanvas = canvasRef.current;
    const drawingCanvas = drawingCanvasRef.current;
    
    if (mainCtx && mainCanvas && drawingCanvas) {
      const displayWidth = mainCanvas.clientWidth;
      const displayHeight = mainCanvas.clientHeight;
      
      // 清空主画布并填充新背景色
      mainCtx.clearRect(0, 0, displayWidth, displayHeight);
      mainCtx.fillStyle = newColor;
      mainCtx.fillRect(0, 0, displayWidth, displayHeight);
      
      // 将离屏画布的手写内容绘制到主画布
      mainCtx.drawImage(drawingCanvas, 0, 0, displayWidth, displayHeight);
      
      // 重新绘制网格
      if (showGrid) {
        drawGrid(mainCtx, displayWidth, displayHeight);
      }
      
      // 保存到历史记录
      saveToHistory();
    }
  };


// 保存手写内容
  const saveDrawing = async () => {
    if (!title.trim()) {
      alert('请输入标题');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      // 将画布转换为base64图片
      const imageData = canvas.toDataURL('image/png');
      
      // 创建备忘录数据
      const memoData = {
        title: title.trim(),
        content: `[手写内容 - ${new Date().toLocaleDateString('zh-CN')}]`,
        handwriting_data: imageData,
        handwriting_mode: true,
        category_id: selectedCategoryId
      };
      
      // 调用保存回调
      if (onSave) {
        await onSave(memoData);
      }
      
    } catch (err) {
      console.error('保存手写内容失败:', err);
      let errorMsg = '保存失败，请检查网络连接';
      if (err.response) {
        if (err.response.data && err.response.data.error) {
          errorMsg = err.response.data.error;
        } else if (err.response.status === 413) {
          errorMsg = '手写内容太大，请减少画布尺寸或简化内容后重试';
        } else if (err.response.status === 500) {
          errorMsg = '服务器内部错误，请稍后重试';
        }
      } else if (err.message) {
        errorMsg = err.message;
      }

      alert(`保存失败: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  // 设置画笔颜色
  const selectColor = (color) => {
    setLineColor(color);
    setDrawingMode('pen');
    setShowColorPicker(false);
    
    // 更新离屏画布的画笔颜色
    const ctx = drawingContextRef.current;
    if (ctx) {
      ctx.strokeStyle = color;
    }
  };

// 导出图片
  const exportImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = `手写备忘录-${title || new Date().toISOString().slice(0, 10)}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const undo = useCallback(() => {
    if (historyIndex <= 0) return;
  
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    
    const img = new Image();
    img.onload = () => {
        const mainCtx = contextRef.current;
        const mainCanvas = canvasRef.current;
        const drawingCtx = drawingContextRef.current;
        const drawingCanvas = drawingCanvasRef.current;
        
        if (mainCtx && mainCanvas && drawingCtx && drawingCanvas) {
            const displayWidth = mainCanvas.clientWidth;
            const displayHeight = mainCanvas.clientHeight;
            
            // 清空离屏画布
            drawingCtx.clearRect(0, 0, displayWidth, displayHeight);
            
            // 将历史图像绘制到离屏画布
            drawingCtx.drawImage(img, 0, 0, displayWidth, displayHeight);
        
        // 重绘到主画布
            redrawToMainCanvas();
        }
    };
    img.src = drawingHistory[newIndex];
  }, [drawingHistory, historyIndex, redrawToMainCanvas]);

// 设置画布事件监听
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    console.log('设置画布事件监听');
    
    // 添加事件监听器
    const handleMouseDown = (e) => startDrawing(e);
    const handleMouseMove = (e) => draw(e);
    const handleMouseUp = () => stopDrawing();
    const handleMouseOut = () => stopDrawing();
    
    const handleTouchStart = (e) => {
      e.preventDefault();
      startDrawing(e);
    };
    const handleTouchMove = (e) => {
      e.preventDefault();
      draw(e);
    };
    const handleTouchEnd = (e) => {
      e.preventDefault();
      stopDrawing();
    };
    
    // 鼠标事件
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseout', handleMouseOut);
    
    // 触摸事件
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseout', handleMouseOut);
      
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [startDrawing, draw, stopDrawing]);

// 添加一个 useEffect 来确保画布就绪后才加载图片
  useEffect(() => {
    if (!memoId) return;
    
    const waitForCanvas = () => {
        const maxWaitTime = 5000; // 最大等待5秒
        const startTime = Date.now();
        
        const checkCanvas = () => {
        const canvas = canvasRef.current;
        const currentTime = Date.now();
        
        if (canvas && canvas.clientWidth > 0 && canvas.clientHeight > 0) {
            console.log('画布已就绪，开始加载图片');
            loadExistingDrawing();
            return;
        }
        
        if (currentTime - startTime > maxWaitTime) {
            console.error('等待画布超时');
            setIsLoading(false);
            return;
        }
        
        console.log('等待画布就绪...');
        setTimeout(checkCanvas, 100);
        };
        
        checkCanvas();
    };
    
    // 延迟开始等待
    setTimeout(waitForCanvas, 300);
  }, [memoId]);


// 在主要初始化 useEffect 之后添加
  useEffect(() => {
    console.log('showGrid 状态变化:', showGrid);
    
    // 当 showGrid 状态变化时，确保画布正确重绘
    if (contextRef.current && canvasRef.current) {
        redrawToMainCanvas();
    }
  }, [showGrid, redrawToMainCanvas]);

  // 键盘快捷键支持
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z 撤销
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Ctrl+Shift+Z 或 Ctrl+Y 重做
      if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') || 
          ((e.ctrlKey || e.metaKey) && e.key === 'y')) {
        e.preventDefault();
        redo();
      }
      // Delete 清空画布
      if (e.key === 'Delete') {
        e.preventDefault();
        clearCanvas();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);


  

  // 组件样式
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '10px',
    },
    container: {
      backgroundColor: theme === 'dark' ? '#161b22' : '#ffffff',
      border: `1px solid ${theme === 'dark' ? '#30363d' : '#d0d7de'}`,
      borderRadius: '12px',
      width: '100%',
      maxWidth: '1000px',
      maxHeight: '95vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    header: {
      padding: '16px 24px',
      borderBottom: `1px solid ${theme === 'dark' ? '#30363d' : '#d0d7de'}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: theme === 'dark' ? '#0d1117' : '#f6f8fa',
      flexWrap: 'wrap',
    },
    titleInput: {
      flex: 1,
      padding: '8px 12px',
      fontSize: '18px',
      fontWeight: '600',
      backgroundColor: 'transparent',
      border: 'none',
      color: theme === 'dark' ? '#e6edf3' : '#24292f',
      outline: 'none',
      minWidth: '200px',
      maxWidth: '100%',
      marginBottom: '10px',
    },
    categorySelector: {
      padding: '8px 12px',
      fontSize: '14px',
      border: `1px solid ${theme === 'dark' ? '#30363d' : '#d0d7de'}`,
      borderRadius: '6px',
      backgroundColor: theme === 'dark' ? '#21262d' : '#ffffff',
      color: theme === 'dark' ? '#c9d1d9' : '#24292f',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      minWidth: '120px',
    },
    mainContent: {
      display: 'flex',
      flex: 1,
      minHeight: '400px',
      overflow: 'hidden',
    },
    toolbar: {
      width: '280px',
      padding: '20px',
      borderRight: `1px solid ${theme === 'dark' ? '#30363d' : '#d0d7de'}`,
      backgroundColor: theme === 'dark' ? '#0d1117' : '#f6f8fa',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      overflowY: 'auto',
    },
    toolSection: {
      marginBottom: '15px',
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '10px',
      color: theme === 'dark' ? '#c9d1d9' : '#57606a',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    canvasContainer: {
      flex: 1,
      position: 'relative',
      overflow: 'auto',
      padding: '20px',
      backgroundColor: backgroundColor,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '400px',
      height: 'calc(100vh - 300px)',
    },
    canvas: {
      backgroundColor: backgroundColor,
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      cursor: drawingMode === 'pen' ? 'crosshair' : 'default',
      touchAction: 'none',
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      display: 'block', // 确保是块级元素
      border: showGrid ? `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` : 'none',
    },
    colorPickerContainer: {
      position: 'absolute',
      top: '100%',
      left: 0,
      marginTop: '5px',
      backgroundColor: theme === 'dark' ? '#21262d' : '#ffffff',
      border: `1px solid ${theme === 'dark' ? '#30363d' : '#d0d7de'}`,
      borderRadius: '8px',
      padding: '10px',
      zIndex: 100,
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '8px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    },
    colorSwatch: {
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      cursor: 'pointer',
      border: `2px solid ${theme === 'dark' ? '#30363d' : '#d0d7de'}`,
      transition: 'transform 0.2s ease',
    },
    toolButton: {
      padding: '10px 16px',
      borderRadius: '8px',
      border: `1px solid ${theme === 'dark' ? '#30363d' : '#d0d7de'}`,
      backgroundColor: theme === 'dark' ? '#21262d' : '#ffffff',
      color: theme === 'dark' ? '#c9d1d9' : '#24292f',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
    },
    toolButtonActive: {
      backgroundColor: theme === 'dark' ? '#58a6ff' : '#0969da',
      color: 'white',
      borderColor: theme === 'dark' ? '#58a6ff' : '#0969da',
    },
    brushSizeOption: {
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      border: `2px solid ${theme === 'dark' ? '#30363d' : '#d0d7de'}`,
      backgroundColor: theme === 'dark' ? '#21262d' : '#ffffff',
      color: theme === 'dark' ? '#c9d1d9' : '#24292f',
    },
    brushSizeActive: {
      borderColor: theme === 'dark' ? '#58a6ff' : '#0969da',
      backgroundColor: theme === 'dark' ? 'rgba(88, 166, 255, 0.1)' : 'rgba(9, 105, 218, 0.1)',
    },
    actionButtons: {
      padding: '20px',
      borderTop: `1px solid ${theme === 'dark' ? '#30363d' : '#d0d7de'}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: theme === 'dark' ? '#0d1117' : '#f6f8fa',
      flexWrap: 'wrap',
    },
    currentColorDisplay: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      border: `2px solid ${theme === 'dark' ? '#c9d1d9' : '#57606a'}`,
      backgroundColor: lineColor,
      cursor: 'pointer',
      marginBottom: '10px',
    },
    statusBar: {
      fontSize: '12px',
      color: theme === 'dark' ? '#8b949e' : '#6e7781',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flexWrap: 'wrap',
    },
  };

  // 获取当前选中的分类信息
  const getSelectedCategoryInfo = () => {
    if (!selectedCategoryId) {
      return { name: '未分类', icon: '📄' };
    }
    const category = availableCategories.find(c => c.id === selectedCategoryId);
    return category || { name: '未分类', icon: '📄' };
  };

  const selectedCategoryInfo = getSelectedCategoryInfo();

  return (
    <div style={styles.overlay}>
      <div style={styles.container}>
        {/* 头部 */}
        <div style={styles.header} className="handwriting-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="输入手写备忘录标题..."
              style={styles.titleInput}
              maxLength={100}
              className="handwriting-title-input"
            />
            
            {/* 分类选择器 */}
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  ...styles.categorySelector,
                  minWidth: '150px',
                  cursor: 'pointer'
                }}
                onClick={() => setShowCategorySelector(!showCategorySelector)}
              >
                <span style={{ fontSize: '18px' }}>
                  {selectedCategoryInfo.icon}
                </span>
                <span style={{ 
                  fontSize: '14px',
                  color: theme === 'dark' ? '#c9d1d9' : '#57606a'
                }}>
                  {selectedCategoryInfo.name}
                </span>
              </div>
              
              {showCategorySelector && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: theme === 'dark' ? '#161b22' : '#ffffff',
                  border: `1px solid ${theme === 'dark' ? '#30363d' : '#d0d7de'}`,
                  borderRadius: '8px',
                  padding: '8px 0',
                  marginTop: '5px',
                  zIndex: 1001,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                }}>
                  <div
                    style={{
                      padding: '8px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      backgroundColor: !selectedCategoryId 
                        ? (theme === 'dark' ? 'rgba(88, 166, 255, 0.1)' : 'rgba(9, 105, 218, 0.1)')
                        : 'transparent'
                    }}
                    onClick={() => {
                      setSelectedCategoryId(null);
                      setShowCategorySelector(false);
                    }}
                  >
                    <span>📄</span>
                    <span>未分类</span>
                  </div>
                  
                  {availableCategories.map(category => (
                    <div
                      key={category.id}
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        backgroundColor: selectedCategoryId === category.id
                          ? (theme === 'dark' ? 'rgba(88, 166, 255, 0.1)' : 'rgba(9, 105, 218, 0.1)')
                          : 'transparent'
                      }}
                      onClick={() => {
                        setSelectedCategoryId(category.id);
                        setShowCategorySelector(false);
                      }}
                    >
                      <span>{category.icon}</span>
                      <span>{category.name}</span>
                      <div 
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: category.color,
                          marginLeft: 'auto'
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div style={styles.statusBar}>
            <span>当前模式: {drawingMode === 'pen' ? '画笔' : '橡皮擦'}</span>
            <span>笔刷大小: {lineWidth}px</span>
            <span>历史记录: {historyIndex + 1}/{drawingHistory.length}</span>
          </div>
        </div>
        
        {/* 主要内容区域 */}
        <div style={styles.mainContent} className="handwriting-main-content">
          {/* 左侧工具栏 */}
          <div style={styles.toolbar} className="handwriting-toolbar">
            {/* 画笔/橡皮擦切换 */}
            <div style={styles.toolSection} className="handwriting-tool-section">
              <div style={styles.sectionTitle}>绘制工具</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  style={{
                    ...styles.toolButton,
                    ...(drawingMode === 'pen' ? styles.toolButtonActive : {})
                  }}
                  onClick={() => setDrawingMode('pen')}
                >
                  <span style={{ fontSize: '18px' }}>✏️</span> 画笔
                </button>
                <button
                  style={{
                    ...styles.toolButton,
                    ...(drawingMode === 'eraser' ? styles.toolButtonActive : {})
                  }}
                  onClick={() => setDrawingMode('eraser')}
                >
                  <span style={{ fontSize: '18px' }}>🧹</span> 橡皮擦
                </button>
              </div>
            </div>
            
            {/* 颜色选择 */}
            <div style={styles.toolSection} className="handwriting-tool-section">
              <div style={styles.sectionTitle}>颜色</div>
              <div style={{ position: 'relative' }}>
                <div 
                  style={styles.currentColorDisplay}
                  onClick={() => setShowColorPicker(!showColorPicker)}
                />
                
                {showColorPicker && (
                  <div style={styles.colorPickerContainer}>
                    {colorPalette.map((color) => (
                      <div
                        key={color}
                        style={{
                          ...styles.colorSwatch,
                          backgroundColor: color,
                          transform: color === lineColor ? 'scale(1.1)' : 'scale(1)',
                          borderColor: color === lineColor 
                            ? (theme === 'dark' ? '#58a6ff' : '#0969da') 
                            : (theme === 'dark' ? '#30363d' : '#d0d7de')
                        }}
                        onClick={() => selectColor(color)}
                        title={color}
                      />
                    ))}
                  </div>
                )}
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginTop: '10px' }}>
                  {colorPalette.map((color) => (
                    <div
                      key={color}
                      style={{
                        ...styles.colorSwatch,
                        backgroundColor: color,
                        transform: color === lineColor ? 'scale(1.1)' : 'scale(1)',
                        borderColor: color === lineColor 
                          ? (theme === 'dark' ? '#58a6ff' : '#0969da') 
                          : (theme === 'dark' ? '#30363d' : '#d0d7de')
                      }}
                      onClick={() => selectColor(color)}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            {/* 笔画粗细 */}
            <div style={styles.toolSection} className="handwriting-tool-section">
              <div style={styles.sectionTitle}>笔画粗细</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {brushSizes.map((size) => (
                  <div
                    key={size}
                    style={{
                      ...styles.brushSizeOption,
                      ...(lineWidth === size ? styles.brushSizeActive : {})
                    }}
                    onClick={() => setLineWidth(size)}
                    title={`${size}px`}
                  >
                    <div 
                      style={{
                        width: size,
                        height: size,
                        borderRadius: '50%',
                        backgroundColor: lineColor,
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* 画布设置 */}
            <div style={styles.toolSection} className="handwriting-tool-section">
              <div style={styles.sectionTitle}>画布设置</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* 背景色选择器 */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {backgroundColors.map((color) => (
                    <div
                      key={color.value}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: color.value,
                        cursor: 'pointer',
                        border: `2px solid ${backgroundColor === color.value 
                          ? (theme === 'dark' ? '#58a6ff' : '#0969da') 
                          : 'transparent'}`,
                        boxShadow: backgroundColor === color.value ? '0 0 0 2px rgba(88, 166, 255, 0.3)' : 'none'
                      }}
                      onClick={() => changeBackgroundColor(color.value)}
                      title={color.name}
                    />
                  ))}
                  {/* 自定义颜色选择器 */}
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => changeBackgroundColor(e.target.value)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      padding: 0,
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    title="自定义颜色"
                  />
                </div>
                
                <button
                  style={styles.toolButton}
                  onClick={toggleGrid}
                >
                  <span style={{ fontSize: '18px' }}>{showGrid ? '✅' : '📐'}</span> {showGrid ? '隐藏网格' : '显示网格'}
                </button>
                <button
                  style={styles.toolButton}
                  onClick={clearCanvas}
                >
                  <span style={{ fontSize: '18px' }}>🗑️</span> 清空画布
                </button>
              </div>
            </div>
            
            {/* 历史记录操作 */}
            <div style={styles.toolSection} className="handwriting-tool-section">
              <div style={styles.sectionTitle}>历史记录</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  style={{
                    ...styles.toolButton,
                    opacity: historyIndex > 0 ? 1 : 0.5,
                    cursor: historyIndex > 0 ? 'pointer' : 'not-allowed'
                  }}
                  onClick={undo}
                  disabled={historyIndex <= 0}
                >
                  <span style={{ fontSize: '18px' }}>↶</span> 撤销 (Ctrl+Z)
                </button>
                <button
                  style={{
                    ...styles.toolButton,
                    opacity: historyIndex < drawingHistory.length - 1 ? 1 : 0.5,
                    cursor: historyIndex < drawingHistory.length - 1 ? 'pointer' : 'not-allowed'
                  }}
                  onClick={redo}
                  disabled={historyIndex >= drawingHistory.length - 1}
                >
                  <span style={{ fontSize: '18px' }}>↷</span> 重做 (Ctrl+Y)
                </button>
              </div>
            </div>
            
            {/* 快捷键提示 */}
            <div style={styles.toolSection} className="handwriting-tool-section">
              <div style={styles.sectionTitle}>快捷键</div>
              <div style={{ fontSize: '12px', color: theme === 'dark' ? '#8b949e' : '#6e7781', lineHeight: '1.5' }}>
                <div>• Ctrl+Z: 撤销</div>
                <div>• Ctrl+Y: 重做</div>
                <div>• Delete: 清空画布</div>
                <div>• 鼠标滚轮: 缩放画布</div>
              </div>
            </div>
            
            {/* 导出功能 */}
            <div style={styles.toolSection} className="handwriting-tool-section">
              <div style={styles.sectionTitle}>导出</div>
              <button
                style={styles.toolButton}
                onClick={exportImage}
              >
                <span style={{ fontSize: '18px' }}>💾</span> 导出为图片
              </button>
            </div>
          </div>
          
          {/* 右侧画布区域 */}
          <div style={styles.canvasContainer} className="handwriting-canvas-container">
            {/* 始终渲染画布，但根据 isLoading 控制显示 */}
            <canvas
            ref={canvasRef}
            style={{
                ...styles.canvas,
                display: isLoading ? 'none' : 'block',
                opacity: isLoading ? 0 : 1,
                transition: 'opacity 0.3s ease'
            }}
            id="handwriting-canvas"
            />
          </div>
        </div>
        
        {/* 底部操作按钮 */}
        <div style={styles.actionButtons} className="handwriting-action-buttons">
          <button
            onClick={onCancel}
            style={{
              ...styles.toolButton,
              backgroundColor: theme === 'dark' ? '#da3633' : '#cf222e',
              color: 'white',
              borderColor: theme === 'dark' ? '#da3633' : '#cf222e',
              marginBottom: '10px',
            }}
          >
            取消
          </button>
          
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <button
              style={styles.toolButton}
              onClick={exportImage}
            >
              导出图片
            </button>
            <button
              onClick={saveDrawing}
              style={{
                ...styles.toolButton,
                ...styles.toolButtonActive,
                opacity: isSaving ? 0.7 : 1,
              }}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="spinner" style={{ width: '16px', height: '16px' }}></span>
                  保存中...
                </>
              ) : (
                '保存手写内容'
              )}
            </button>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* 移动端响应式优化 */
        @media (max-width: 768px) {
          .handwriting-main-content {
            flex-direction: column !important;
          }
          
          .handwriting-toolbar {
            width: 100% !important;
            max-height: 200px !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            padding: 10px !important;
            border-right: none !important;
            border-bottom: 1px solid ${theme === 'dark' ? '#30363d' : '#d0d7de'} !important;
          }
          
          .handwriting-tool-section {
            margin-bottom: 0 !important;
            margin-right: 15px !important;
            min-width: 150px !important;
          }
          
          .handwriting-canvas-container {
            min-height: 300px !important;
            height: 50vh !important;
            padding: 10px !important;
          }
          
          .handwriting-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 10px !important;
          }
          
          .handwriting-title-input {
            max-width: 100% !important;
            width: 100% !important;
            margin-bottom: 10px !important;
          }
          
          .handwriting-action-buttons {
            flex-direction: column !important;
            gap: 10px !important;
          }
          
          .handwriting-action-buttons > div {
            width: 100% !important;
            display: flex !important;
            justify-content: space-between !important;
          }
        }
        
        @media (max-width: 480px) {
          .handwriting-toolbar {
            max-height: 150px !important;
          }
          
          .handwriting-tool-section {
            min-width: 120px !important;
          }
          
          .handwriting-canvas-container {
            height: 40vh !important;
          }
        }
        
        /* 触摸设备优化 */
        @media (hover: none) and (pointer: coarse) {
          .handwriting-canvas-container canvas {
            cursor: default;
          }
          
          .handwriting-toolbar button {
            min-height: 44px !important;
            min-width: 44px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default HandwritingPanel;